import { useTranslation } from 'react-i18next'
import { List, Grid, Clock, GitBranch } from 'lucide-react'
import type { CanvasViewMode } from './types'

interface ViewOption {
  mode: CanvasViewMode
  icon: React.ReactNode
  label: string
  enabled: boolean
}

interface CanvasViewToggleProps {
  activeView: CanvasViewMode
  onSetView: (view: CanvasViewMode) => void
}

const DISABLED_OPACITY = 0.5

export function CanvasViewToggle({
  activeView,
  onSetView,
}: CanvasViewToggleProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const viewOptions: ViewOption[] = [
    {
      mode: 'table',
      icon: <List className="h-4 w-4" />,
      label: t('canvas.viewToggle.table'),
      enabled: true,
    },
    {
      mode: 'gallery',
      icon: <Grid className="h-4 w-4" />,
      label: t('canvas.viewToggle.gallery'),
      enabled: false,
    },
    {
      mode: 'timeline',
      icon: <Clock className="h-4 w-4" />,
      label: t('canvas.viewToggle.timeline'),
      enabled: false,
    },
    {
      mode: 'graph',
      icon: <GitBranch className="h-4 w-4" />,
      label: t('canvas.viewToggle.graph'),
      enabled: false,
    },
  ]

  return (
    <div className="flex gap-1">
      {viewOptions.map((opt) => (
        <button
          key={opt.mode}
          onClick={() => {
            if (opt.enabled) {
              onSetView(opt.mode)
            }
          }}
          aria-label={opt.label}
          disabled={!opt.enabled}
          className="rounded p-1.5 transition-colors"
          style={{
            color: activeView === opt.mode ? 'var(--accent)' : 'var(--text-muted)',
            background: activeView === opt.mode ? 'var(--accent-dim)' : 'transparent',
            opacity: opt.enabled ? 1 : DISABLED_OPACITY,
            cursor: opt.enabled ? 'pointer' : 'not-allowed',
          }}
        >
          {opt.icon}
        </button>
      ))}
    </div>
  )
}
